import { CallMedium } from '@zoopla-eng/zds-components/icons';
import { LinkWithIcon } from '@zoopla-eng/zds-components/link';
import { fontGroups } from '@zoopla-eng/zds-components/themes';
import { Spinner } from '@zoopla/assets';
import { getDynamicNumber } from '@zoopla/dynamic-phone-number';
import { queries } from '@zoopla-eng/zds-components/themes';
import { useBreakpointListener } from '@zoopla/dom-util';
import React, { useEffect, useRef, useState } from 'react';
import {
    contactLinkStyle,
    contactWrapperStyle,
    loaderStyle
} from './contact.styles.css';

export type ContactPhoneProps = {
    phoneNumber?: string | null;
    branchId: number;
    listingId: number;
    onRevealPhoneNumberClick?: () => void;
    onPhoneNumberClick?: () => void;
};

type State =
    | { type: 'loading' }
    | { type: 'error' }
    | { type: 'phone'; value: string };

const ContactPhone: React.FC<ContactPhoneProps> = ({
    phoneNumber,
    branchId,
    listingId,
    onRevealPhoneNumberClick,
    onPhoneNumberClick
}) => {
    const [state, setState] = useState<State | null>(null);
    const callLinkRef = useRef<HTMLAnchorElement>(null);
    const isMobile = useBreakpointListener(queries.M[100]);

    const getCallText = () => {
        if (state?.type === 'loading') {
            return <img className={loaderStyle} src={Spinner} alt="loading" />;
        }
        if (state?.type === 'phone' && !isMobile) {
            return state.value;
        }
        return 'Call';
    };

    const handleIovoxClick = async () => {
        onRevealPhoneNumberClick?.();

        setState({ type: 'loading' });

        Promise.race([
            getDynamicNumber(branchId, listingId),
            new Promise<{ type: 'timeout' }>((res) =>
                setTimeout(() => res({ type: 'timeout' }), 2000)
            )
        ]).then(
            (success) => {
                if (success.type === 'phone') {
                    setState(success);
                } else {
                    setState(
                        phoneNumber
                            ? { type: 'phone', value: phoneNumber }
                            : null
                    );
                }
            },
            () => {
                setState(
                    phoneNumber ? { type: 'phone', value: phoneNumber } : null
                );
            }
        );
    };

    const shouldForceClick = isMobile && state?.type === 'phone';
    useEffect(() => {
        if (shouldForceClick) {
            callLinkRef.current?.click();
        }
    }, [shouldForceClick]);

    if (!phoneNumber) {
        return null;
    }
    try {
        return (
            <div className={contactWrapperStyle}>
                <div className={`${contactLinkStyle} ${fontGroups.ladle}`}>
                    {state?.type === 'phone' ? (
                        <LinkWithIcon
                            href={`tel:${state.value}`}
                            onClick={onPhoneNumberClick}
                            aria-label="Call agent"
                            iconPrefix={<CallMedium />}
                            ref={callLinkRef}
                        >
                            {getCallText()}
                        </LinkWithIcon>
                    ) : (
                        <LinkWithIcon
                            onClick={handleIovoxClick}
                            aria-label="Reveal agents telephone number"
                            iconPrefix={<CallMedium />}
                        >
                            {getCallText()}
                        </LinkWithIcon>
                    )}
                </div>
            </div>
        );
    } catch (e) {
        return null;
    }
};

export { ContactPhone };
